/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/** Protocol-level constants shared by the decoder and the encoder. */
public object Fit {
    public const val PROFILE_VERSION_SCALE: Int = 1000
    public const val PROFILE_MAJOR_VERSION: Int = 21
    public const val PROFILE_MINOR_VERSION: Int = 218

    /** Profile version as encoded in the FIT file header. */
    public val PROFILE_VERSION: UShort =
        (PROFILE_MAJOR_VERSION * PROFILE_VERSION_SCALE + PROFILE_MINOR_VERSION).toUShort()

    /** Seconds between the UNIX epoch and the FIT epoch (1989-12-31T00:00:00Z). */
    public const val EPOCH_OFFSET_SECONDS: Long = 631065600L

    internal const val FIELD_DEFAULT_SCALE: Double = 1.0
    internal const val FIELD_DEFAULT_OFFSET: Double = 0.0

    /** A field is length-prefixed by a single byte, so it can never exceed 255 bytes. */
    internal const val MAX_FIELD_SIZE: Int = 255

    /** One byte of the maximum is reserved for the NUL terminator appended to every string. */
    internal const val STRING_MAX_BYTE_COUNT: Int = MAX_FIELD_SIZE - 1

    internal const val COMPRESSED_HEADER_MASK: Int = 0x80
    internal const val MESG_DEFINITION_MASK: Int = 0x40
    internal const val MESG_HEADER_MASK: Int = 0x00
    internal const val CRC_SIZE: Int = 2
    internal const val NUM_LOCAL_MESG_DEFS: Int = 16
    internal const val HDR_TYPE_MASK: Int = 0x0F
    internal const val HDR_TYPE_DEF_BIT: Int = 0x40
    internal const val DEV_DATA_MASK: Int = 0x20
    internal const val LOCAL_MESG_NUM_MASK: Int = 0x0F
    internal const val HEADER_WITH_CRC_SIZE: Int = 14
    internal const val HEADER_WITHOUT_CRC_SIZE: Int = 12
}

/**
 * Selects which of a field's meanings to read or write.
 *
 * A FIT field can carry a different meaning depending on a sibling field's value
 * (`event_data` means `gear_change_data` when `event` is `rear_gear_change`).
 * Those alternatives are the field's *subfields*.
 */
public sealed interface SubFieldSelector {
    /** Ignore subfields and use the field's own type, scale and offset. */
    public data object MainField : SubFieldSelector

    /** Use whichever subfield the surrounding message currently satisfies, if any. */
    public data object Active : SubFieldSelector

    /** Use the subfield at this position in the field's subfield list. */
    public data class AtIndex(val index: Int) : SubFieldSelector

    /** Use the subfield with this profile name. */
    public data class Named(val name: String) : SubFieldSelector
}

/** Byte order of a message's field values, declared per message definition. */
public enum class Endianness(public val value: UByte) {
    LITTLE(0u),
    BIG(1u),
    ;

    public companion object {
        public fun fromValue(value: UByte): Endianness? = if (value == 0u.toUByte()) LITTLE else if (value == 1u.toUByte()) BIG else null
    }
}

/** FIT protocol version from the file header. */
public enum class ProtocolVersion(public val value: UByte) {
    INVALID(0x00u),
    V1_0(0x10u),
    V2_0(0x20u),
    ;

    public companion object {
        private val byValue = entries.associateBy { it.value }

        public fun fromValue(value: UByte): ProtocolVersion = byValue[value] ?: INVALID
    }
}
